import { type LineItem, PackageEntrySource } from "../../models/CartData";
import { newChangeObject } from "../../../../../utils/cartUpdating/common";
import {
  refreshAvailabilityLoadContract,
  refreshAvailabilityLoadNonContract,
} from "../../../../../utils/cartUpdating/refreshPackage";
import { requestAvailabilityData, requestPriceData } from "~/domains/product/redux/actions";
import instance from "../../../../../utils/i18n";
import { addMessage } from "~/features/toasts/redux/actions";
import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "~/app/store/types";
import { groupByFn, toMapFn } from "~/common/utils/reducers";
import { getContractDetails } from "~/features/contracts/api/getContractDetails";
import { refreshContractDetailsLoad } from "../../../../../utils/cartUpdating/refreshFromContract";
import { type LineItemBuilder } from "../../../../../utils/cartUpdating/LineItemUpdate";
import { refreshPriceInfoData } from "../../../../../utils/cartUpdating/refreshPriceInfoData";
import { refreshDeliveryDate } from "../../../../../utils/cartUpdating/delivery/refreshDate";
import { refreshDeliveriesForProduct } from "../../../../../utils/cartUpdating/delivery/refreshEdd";
import { sendToHybris, sendToRedux } from "./cartQuoteCommonActions";
import { type RootState } from "~/app/store";
import { validatePackage, validateQty } from "../../../../../utils/cartUpdating/validations/validateGeneralLineItem";
import { validateEntireProductQty } from "../../../../../utils/cartUpdating/validations/validateGeneralProduct";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import { isConfiguratorProduct } from "~/domains/product/types/ProductData";
import type { ContractDetailsResponse } from "~/features/contracts/models/models";

const CONTRACT_DETAILS_KEY_FN = (item: LineItemBuilder) =>
  `${item.get("contractNumber")}-${item.get("contractLine")}-${item.get("packCode")}`;

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export function orchestratePnARefresh(packageEntries: LineItem[], forceRefresh?: boolean): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const productCode = packageEntries[0].productCode;
    const base = getState().products.basic[productCode];

    if (isConfiguratorProduct(base)) {
      return dispatch(orchestratePnARefreshLinear(packageEntries, forceRefresh));
    } else {
      return dispatch(orchestratePnARefreshRegular(packageEntries, forceRefresh));
    }
  };
}

function orchestratePnARefreshLinear(packageEntries: LineItem[], forceRefresh?: boolean): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const productCode = packageEntries[0].productCode;

    const allLineItems = packageEntries.map(newChangeObject);

    // Fetch Prices
    const priceReferences: Dict<boolean> = {};
    allLineItems.forEach((entry) => (priceReferences[entry.get("priceReference")] = true));
    const pPromises = Object.keys(priceReferences).map((priceReference) =>
      dispatch(requestPriceData(productCode, priceReference, forceRefresh)),
    );

    dispatch(requestAvailabilityData(productCode, forceRefresh));

    // Let's wait for the actual data
    const prices = await Promise.all(pPromises);

    // Show a notification that the prices may have changed...
    const isFresh = prices.flat().some((pInfo) => pInfo.isFreshPnaCall);
    if (isFresh) {
      const message = instance.t("cartPage.globalMsg.freshPna");
      const hasFreshMessage = getState().globalMessages.some((msg) => msg.message.value === message);
      if (!hasFreshMessage) {
        dispatch(addMessage("info", message));
      }
    }

    // Let the fun with refreshing begin...
    const state = getState();
    const descriptorsByPriceReference = toMapFn(
      Object.keys(priceReferences).map((priceReference) =>
        selectProductDescriptor(state, { productCode, priceReference }),
      ),
      (descriptor) => descriptor.priceReference,
    );

    // 2. Take care of non contract items.

    // 2.2. Then data from PriceInfo objects (price & Country of Origin).
    refreshPriceInfoData(allLineItems, descriptorsByPriceReference);

    // 5. Various qty and package validations
    allLineItems.forEach(validateQty);
    validateEntireProductQty(allLineItems, Object.values(descriptorsByPriceReference)[0]);
    allLineItems.forEach(validatePackage);

    dispatch(sendToRedux(allLineItems));
    dispatch(sendToHybris(allLineItems));
  };
}

function orchestratePnARefreshRegular(packageEntries: LineItem[], forceRefresh?: boolean): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const productCode = packageEntries[0].productCode;
    const productDesciptor = selectProductDescriptor(getState(), { productCode, priceReference: "" });
    const base = productDesciptor.base;

    const allLineItems = packageEntries.map(newChangeObject);
    const lineItemsByType = groupByFn(
      allLineItems,
      (item) => item.get("source"),
      (item) => item,
    );
    const lineItemsWithUpdateablePrice = [
      ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
      ...(lineItemsByType[PackageEntrySource.BULK] || []),
    ];
    const lineItemsWithUpdateableEDD = [
      ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
      ...(lineItemsByType[PackageEntrySource.BULK] || []),
      ...(lineItemsByType[PackageEntrySource.QUOTE] || []),
    ];
    const contractLineItems = [...(lineItemsByType[PackageEntrySource.CONTRACT] || [])];

    const is15DigitACall = allLineItems.some((lineItem) => lineItem.get("source") === PackageEntrySource.QUOTE);
    // We assume that all line items will have the same pack code in this case. This is normally a PMA product but the quote provided a packCode
    const fifteenDigitPackCode = is15DigitACall ? allLineItems[0]?.get("packCode") : undefined;

    // Fetch Availability
    const aPromise = dispatch(requestAvailabilityData(productCode, forceRefresh, fifteenDigitPackCode));

    // Fetch Prices
    const priceReferences: Dict<boolean> = {};
    allLineItems.forEach((entry) => (priceReferences[entry.get("priceReference")] = true));
    const pPromises = Object.keys(priceReferences).map((priceReference) =>
      dispatch(requestPriceData(productCode, priceReference, forceRefresh, fifteenDigitPackCode)),
    );

    // Fetch Contract Details
    const byContractDetails = groupByFn(contractLineItems, CONTRACT_DETAILS_KEY_FN);
    const contractPromises = Object.values(byContractDetails)
      .map((list) => list[0])
      .map((lineItem) =>
        getContractDetails(
          lineItem.get("contractNumber"),
          base.sapMaterialNumber,
          lineItem.get("packCode"),
          +lineItem.get("contractLine"),
        ),
      );

    // Let's wait for the actual data
    const availability = await aPromise;
    const prices = await Promise.all(pPromises);
    const contractDetails = await Promise.all(contractPromises);

    // Show a notification that the prices may have changed...
    const isFresh = availability!.isFreshPnaCall || prices.flat().some((pInfo) => pInfo.isFreshPnaCall);
    if (isFresh) {
      const message = instance.t("cartPage.globalMsg.freshPna");
      const hasFreshMessage = getState().globalMessages.some((msg) => msg.message.value === message);
      if (!hasFreshMessage) {
        dispatch(addMessage("info", message));
      }
    }

    // Let the fun with refreshing begin...
    const state = getState();
    const descriptorsByPriceReference = toMapFn(
      Object.keys(priceReferences).map((priceReference) =>
        selectProductDescriptor(state, { productCode, priceReference }),
      ),
      (descriptor) => descriptor.priceReference,
    );

    // 1. Take care of contract items. They are simple(r)
    const contractDetailsByKey = recreateContractDetailsMap(byContractDetails, contractDetails);

    contractLineItems.forEach((lineItem) =>
      refreshContractDetailsLoad(
        lineItem,
        descriptorsByPriceReference[lineItem.get("priceReference")],
        contractDetailsByKey[CONTRACT_DETAILS_KEY_FN(lineItem)]!,
      ),
    );

    contractLineItems.forEach((lineItem) =>
      refreshAvailabilityLoadContract(
        lineItem,
        descriptorsByPriceReference[lineItem.get("priceReference")].availability!,
      ),
    );

    // 2. Take care of non contract items.

    // 2.1. First availability => We may need to preselect packages.
    lineItemsWithUpdateablePrice.forEach((lineItem) => {
      const descriptor = descriptorsByPriceReference[lineItem.get("priceReference")];
      refreshAvailabilityLoadNonContract(lineItem, descriptor.availability!, descriptor);
    });

    // 2.2. Then data from PriceInfo objects (price & Country of Origin).
    refreshPriceInfoData(allLineItems, descriptorsByPriceReference);

    // 3. Refresh the Estimated Delivery Date.
    refreshDeliveriesForProduct(lineItemsWithUpdateableEDD, availability!);

    // 4. Refresh requested delivery date
    allLineItems.forEach(refreshDeliveryDate);

    // 4b. Repeat - Refresh the Estimated Delivery Date.
    refreshDeliveriesForProduct(lineItemsWithUpdateableEDD, availability!);

    // 5. Various qty and package validations
    allLineItems.forEach(validateQty);
    validateEntireProductQty(allLineItems, Object.values(descriptorsByPriceReference)[0]);
    allLineItems.forEach(validatePackage);

    dispatch(sendToRedux(allLineItems));
    dispatch(sendToHybris(allLineItems));
  };
}

export function orchestrateARefreshOnCheckout(
  packageEntries: LineItem[],
  forceRefresh?: boolean,
): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const productCode = packageEntries[0].productCode;

    // Nothing to refresh for linear products
    // Fixes SCHAEFF-12563
    const productData = getState().products.basic[productCode];
    if (isConfiguratorProduct(productData)) {
      dispatch(requestAvailabilityData(productCode, forceRefresh));
      return;
    }

    const allLineItems = packageEntries.map(newChangeObject);
    const lineItemsByType = groupByFn(
      allLineItems,
      (item) => item.get("source"),
      (item) => item,
    );
    const lineItemsWithUpdateableEDD = [
      ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
      ...(lineItemsByType[PackageEntrySource.BULK] || []),
      ...(lineItemsByType[PackageEntrySource.QUOTE] || []),
    ];

    const is15DigitACall = allLineItems.some((lineItem) => lineItem.get("source") === PackageEntrySource.QUOTE);
    // We assume that all line items will have the same pack code in this case. This is normally a PMA product but the quote provided a packCode
    const fifteenDigitPackCode = is15DigitACall ? allLineItems[0]?.get("packCode") : undefined;

    // Fetch Availability
    const aPromise = dispatch(requestAvailabilityData(productCode, forceRefresh, fifteenDigitPackCode));

    // SCHAEFF-16550 - Request prices for all products, so that contract icon can appear..
    const priceReferences: Dict<boolean> = {};
    allLineItems.forEach((entry) => (priceReferences[entry.get("priceReference")] = true));
    const pPromises = Object.keys(priceReferences).map((priceReference) =>
      dispatch(requestPriceData(productCode, priceReference, forceRefresh, fifteenDigitPackCode)),
    );

    // Let's wait for the actual data
    const availability = await aPromise;

    // 3. Refresh the Estimated Delivery Date.
    refreshDeliveriesForProduct(lineItemsWithUpdateableEDD, availability!);

    dispatch(sendToRedux(allLineItems));
    // dispatch(sendToHybris(allLineItems));
  };
}

function recreateContractDetailsMap(
  byContractDetails: Dict<LineItemBuilder[]>,
  contractDetails: (ContractDetailsResponse | null)[],
): Dict<ContractDetailsResponse | null> {
  return Object.keys(byContractDetails)
    .map((key, index) => [key, index] as [string, number])
    .reduce(
      (map, [key, index]) => {
        map[key] = contractDetails[index];
        return map;
      },
      {} as Dict<ContractDetailsResponse | null>,
    );
}
